import { IIdentityContext } from '@almamedia-legal-insights/types';
import { Typography, Box, Stack, Divider, Link, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { JURISTILIITTO_CONNECTION_ID } from '../../src/components/Auth/AuthProvider';
import { FeatureFlag } from '../../src/common/components/FeatureFlag';

export default function AccountMenuContent({
  identityContext,
  closeMenu,
}: Readonly<{
  identityContext: IIdentityContext;
  closeMenu: () => void;
}>) {
  const { t } = useTranslation();
  const { authInfo, user, loading, actions } = identityContext;
  const isIpAuthenticated = Boolean(authInfo?.ipAuth);
  const organization = authInfo?.organization;
  const isAuthenticatedUser = user !== undefined;
  const showLoginOptions = !isAuthenticatedUser && !isIpAuthenticated;

  const handleLoginRegular = () => {
    actions?.login();
    closeMenu();
  };

  const handleLoginJuristiliitto = () => {
    if (actions?.login) {
      actions.login(JURISTILIITTO_CONNECTION_ID);
      closeMenu();
    } else {
      console.info('Actions not yet loaded');
    }
  };

  return (
    <Stack
      direction='column'
      sx={{
        width: { xs: '100%', sm: 280 },
        maxWidth: '100%',
        paddingX: 2,
        paddingY: 2,
        boxSizing: 'border-box',
      }}
      spacing={1.5}
    >
      {isIpAuthenticated && !isAuthenticatedUser && (
        <Box sx={{ paddingX: 1.5 }}>
          <Typography
            variant='body2'
            sx={{ marginBottom: 0.5, color: 'text.secondary' }}
          >
            {t('accountMenu.ipAccess')}
          </Typography>
          {!loading && <Typography>{organization}</Typography>}
          <Divider sx={{ marginY: 1.5 }} />
        </Box>
      )}

      {isAuthenticatedUser && (
        <Box sx={{ paddingX: 1.5 }}>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {t('accountMenu.loggedInAs')}
          </Typography>
          <Typography
            variant='subtitle2'
            sx={{ wordBreak: 'break-all', marginBottom: 1.5 }}
          >
            {user?.email}
          </Typography>
          <Link
            variant='body2'
            href='#'
            underline='none'
            sx={{
              display: 'block',
              color: 'text.secondary',
              '&:hover': { textDecoration: 'underline' },
            }}
            onClick={() => {
              actions?.logout();
              closeMenu();
            }}
          >
            {t('accountMenu.logOut')}
          </Link>
        </Box>
      )}

      {showLoginOptions && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5,
            marginX: 0.5,
          }}
        >
          <Button variant='contained' fullWidth onClick={handleLoginRegular}>
            {t('accountMenu.loginRegular')}
          </Button>
          {authInfo && (
            <FeatureFlag flagName='juristiliitto-login' authInfo={authInfo}>
              <Button
                variant='outlined'
                fullWidth
                onClick={handleLoginJuristiliitto}
              >
                {t('accountMenu.loginJuristiliitto')}
              </Button>
            </FeatureFlag>
          )}
        </Box>
      )}

      {isIpAuthenticated && !isAuthenticatedUser && (
        <>
          <Typography
            variant='body2'
            sx={{ color: 'text.secondary', mt: 1, paddingX: 1.5 }}
          >
            {t('accountMenu.ipAuthLoginPrompt')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
              mt: 1,
              marginX: 0.5,
            }}
          >
            <Button variant='contained' fullWidth onClick={handleLoginRegular}>
              {t('accountMenu.loginRegular')}
            </Button>
            {authInfo && (
              <FeatureFlag flagName='juristiliitto-login' authInfo={authInfo}>
                <Button
                  variant='outlined'
                  fullWidth
                  onClick={handleLoginJuristiliitto}
                >
                  {t('accountMenu.loginJuristiliitto')}
                </Button>
              </FeatureFlag>
            )}
          </Box>
        </>
      )}

      {/* Customer Support Section */}
      <Box sx={{ paddingX: 1.5 }}>
        <Divider sx={{ marginY: 1.5 }} />

        <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
          {t('accountMenu.customerSupport')}
        </Typography>
        <Stack direction='column' spacing={0.5}>
          <Link
            href='mailto:sisaltopalvelut@almainsights.fi'
            underline='hover'
            variant='body2'
            sx={{ color: 'primary.main' }}
          >
            {t('accountMenu.customerSupportEmail')}
          </Link>
          <Typography
            variant='body2'
            sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
          >
            {t('accountMenu.customerSupportHours')}
          </Typography>
        </Stack>
      </Box>
    </Stack>
  );
}
